import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { LatLngTuple } from 'leaflet'
import { debounce } from 'lodash'
import { useRecoilState } from 'recoil'
import { floorDetailState, listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'
import Krpano from 'utils/krpano'

export default function useFloorMap({
  initialZoomOut = false,
  setSelectedPointID
}: {
  initialZoomOut?: boolean
  setSelectedPointID: Dispatch<SetStateAction<string>>
}) {
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [points] = useRecoilState(listPointState)

  // State management
  const [isZoomOutMap, setIsZoomOutMap] = useState(initialZoomOut)
  const [imgBounds, setImgBounds] = useState<LatLngTuple>([1000, 1000])
  const [isMapLandscape, setIsMapLandscape] = useState(false)
  const [aspectRatio, setAspectRatio] = useState(1)
  const [isMapLoading, setIsMapLoading] = useState(false)
  const [showMap, setShowMap] = useState(true)
  const isFloorChange = useRef<any>(false)
  const povMarkerRef = useRef<any>(null)
  const radarDragFlag = useRef<any>({
    isDragging: false,
    rotate: null,
    hgap: null,
    mouseHoldTime: null
  })
  const [floorDetail, setFloorDetail] = useRecoilState(floorDetailState)
  const [zoomValue, setZoomValue] = useState({
    zoom: 0,
    minZoom: 0,
    maxZoom: 5
  })
  // state for is SP screen
  const matches = useMediaQuery('(min-width: 768px)')

  // Refs
  const mapContainerRef = useRef<any>(null)
  const mapInitializedRef = useRef<boolean>(false)
  const zoomTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Calculate map size style
  const getMapSizeInlineStyle = useCallback(() => {
    // Base styles for all cases
    const baseStyles = {
      transition:
        'opacity 400ms ease-in-out, transform 600ms ease-in-out, width 600ms ease, height 600ms ease',
      willChange: 'transform, width, height',
      padding: '0.3rem',
      overflow: 'visible',
      opacity: isMapLoading ? 0.1 : 1
    }
    // If aspectRatio is invalid, fallback to 1
    const safeAspectRatio =
      typeof aspectRatio === 'number' && aspectRatio > 0 ? aspectRatio : 1

    // If map is minimized (not zoomed out)
    if (!isZoomOutMap) {
      // Calculate size based on screen ratio and orientation
      const mapWidth = Math.round(window.innerWidth * 0.1) // Always 10% of screen width

      let mapHeight
      if (isMapLandscape) {
        // Landscape: height = width / ratio
        mapHeight = Math.round(mapWidth / safeAspectRatio)

        // Ensure minimum height is 15% of screen height
        const minHeight = window.innerHeight * 0.15
        if (mapHeight < minHeight) {
          mapHeight = Math.round(minHeight)
        }
      } else {
        // Portrait: height = width / ratio
        mapHeight = Math.round(mapWidth / safeAspectRatio)
      }

      return {
        ...baseStyles,
        height: `${mapHeight}px`
      }
    }

    // If map is maximized (zoomed out)
    if (isMapLandscape) {
      // For landscape orientation
      let mapHeight = Math.round(
        (window.innerWidth * (matches ? 0.7 : 0.8)) / safeAspectRatio
      )

      // Limit height to 60% of screen height if it exceeds that
      const maxHeight = window.innerHeight * 0.6
      if (mapHeight > maxHeight) {
        mapHeight = Math.round(maxHeight)
      }

      return {
        ...baseStyles,
        width: window.innerWidth * (matches ? 0.7 : 0.8),
        height: mapHeight
      }
    }

    // For portrait orientation
    const mapHeight = Math.round(window.innerHeight * 0.7)
    // const mapWidth = Math.round(mapHeight * safeAspectRatio)

    return {
      ...baseStyles,
      height: `${mapHeight}px`,
      width: 'auto'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZoomOutMap, isMapLandscape, aspectRatio, isMapLoading])

  // Handle smooth transition
  const animateMapChange = useCallback(
    (callback: () => void) => {
      // Clear any existing zoom timers
      if (zoomTimerRef?.current) {
        clearTimeout(zoomTimerRef?.current)
      }

      setIsMapLoading(true)
      setShowMap(false)

      // Delay to prepare for animation
      zoomTimerRef.current = setTimeout(() => {
        // Execute callback (like changing zoom state)
        callback()

        // Delay to let DOM update with new styles
        zoomTimerRef.current = setTimeout(() => {
          if (mapContainerRef?.current) {
            // Redraw map with new size
            mapContainerRef?.current.invalidateSize({ animate: false })

            // Fit bounds to ensure content is properly displayed
            if (imgBounds[0] > 0 && imgBounds[1] > 0) {
              const bounds = [
                [0, 0],
                [imgBounds[0], imgBounds[1]]
              ]

              mapContainerRef?.current.setMinZoom(-999)
              mapContainerRef?.current.fitBounds(bounds, {
                animate: false,
                duration: 0,
                easeLinearity: 0.5
              })
              mapContainerRef?.current.setMaxBounds(bounds)

              // Set zoom levels
              const minZoomLevel =
                mapContainerRef?.current.getBoundsZoom(bounds)
              mapContainerRef?.current.setView(
                [imgBounds[0] / 2, imgBounds[1] / 2],
                minZoomLevel,
                {
                  animate: false
                }
              )
              // Set zoom constraints
              mapContainerRef?.current.setMinZoom(minZoomLevel)
              mapContainerRef?.current.setMaxZoom(minZoomLevel + 5)
              setZoomValue({
                zoom: minZoomLevel,
                minZoom: minZoomLevel,
                maxZoom: minZoomLevel + 5
              })

              // Force another invalidate after bounds are set
              requestAnimationFrame(() => {
                if (mapContainerRef?.current) {
                  mapContainerRef?.current.invalidateSize({ animate: false })
                }
              })
            }
          }

          // Show map with a delay
          setShowMap(true)

          // Stop loading state after animation completes
          zoomTimerRef.current = setTimeout(() => {
            setIsMapLoading(false)
            // Final invalidate to ensure correct size
            if (mapContainerRef?.current) {
              requestAnimationFrame(() => {
                mapContainerRef?.current.invalidateSize({ animate: true })
              })
            }
          }, 1000)
        }, 1000)
      }, 1000)
    },
    [imgBounds]
  )

  // Toggle zoom
  const handleZoomToggle = useCallback(() => {
    animateMapChange(() => {
      setIsZoomOutMap((prev) => !prev)
    })
  }, [animateMapChange])

  // Initialize map
  const initializeMap = useCallback(() => {
    if (
      !floorDetail?.mapFile?.s3Path ||
      !mapContainerRef?.current ||
      mapInitializedRef.current
    )
      return

    setIsMapLoading(true)
    mapInitializedRef.current = true

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = floorDetail?.mapFile?.s3Path

    img.onload = () => {
      const { width, height } = img

      // Determine orientation
      setIsMapLandscape(width > height)
      setAspectRatio(width / height)

      const bounds = [
        [0, 0],
        [height, width]
      ]
      setIsMapLoading(false)

      // Configure map
      mapContainerRef?.current.setMinZoom(-999)
      mapContainerRef?.current.fitBounds(bounds, {
        animate: false
      })
      mapContainerRef?.current.setMaxBounds(bounds)

      // Set zoom levels
      const minZoomLevel = mapContainerRef?.current.getBoundsZoom(bounds)
      mapContainerRef?.current.setView([height / 2, width / 2], minZoomLevel, {
        animate: false
      })
      // Set zoom constraints
      mapContainerRef?.current.setMinZoom(minZoomLevel)
      mapContainerRef?.current.setMaxZoom(minZoomLevel + 5)
      setZoomValue({
        zoom: minZoomLevel,
        minZoom: minZoomLevel,
        maxZoom: minZoomLevel + 5
      })

      // Update bounds for later reference
      setImgBounds([height, width])

      // DEBUG: measure container before fitBounds
      requestAnimationFrame(() => {
        const minZoomLevel = mapContainerRef?.current.getBoundsZoom(bounds)
        mapContainerRef?.current.setMinZoom(minZoomLevel - 1)
        mapContainerRef?.current.setMaxZoom(minZoomLevel + 5)
      })

      // Finish initialization
      setTimeout(() => {
        mapContainerRef?.current.invalidateSize()
        setIsMapLoading(false)
      }, 500)
    }

    img.onerror = () => {
      setImgBounds([1000, 1000])
      setIsMapLoading(false)
      mapInitializedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorDetail?.mapFile?.s3Path, mapContainerRef?.current])

  // Reset map when floor changes
  const resetMap = useCallback(() => {
    setTimeout(() => {
      if (floorDetail?.mapFile?.s3Path) {
        mapInitializedRef.current = false
      }
    }, 1000)
  }, [floorDetail?.mapFile?.s3Path])

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    if (!mapContainerRef?.current) return

    const zoom = mapContainerRef?.current
      ? +mapContainerRef.current.getZoom().toFixed(2)
      : 0
    const maxZoom = mapContainerRef?.current
      ? +mapContainerRef.current.getMaxZoom().toFixed(2)
      : 0
    mapContainerRef?.current.zoomIn(1, {
      animate: true,
      duration: 0.5
    })
    if (zoom < maxZoom) {
      setZoomValue((prev) => ({ ...prev, zoom: zoom + 1 }))
    }
  }, [])

  const handleZoomOut = useCallback(() => {
    if (!mapContainerRef?.current) return

    const zoom = mapContainerRef?.current
      ? +mapContainerRef.current.getZoom().toFixed(2)
      : 0
    const minZoom = mapContainerRef?.current
      ? +mapContainerRef.current.getMinZoom().toFixed(2)
      : 0
    mapContainerRef?.current.zoomOut(1, {
      animate: true,
      duration: 0.5
    })
    if (zoom > minZoom) {
      setZoomValue((prev) => ({ ...prev, zoom: zoom - 1 }))
    }
  }, [])

  const handleZoomToPoint = useCallback((x: number, y: number) => {
    if (!mapContainerRef?.current) return

    const zoom = mapContainerRef?.current
      ? +mapContainerRef.current.getZoom().toFixed(2)
      : 0
    const maxZoom = mapContainerRef?.current
      ? +mapContainerRef.current.getMaxZoom().toFixed(2)
      : 0
    mapContainerRef?.current.setView([y, x], zoom + 1, {
      animate: true,
      duration: 0.5,
      easeLinearity: 0.5
    })
    if (zoom < maxZoom) {
      setZoomValue((prev) => ({ ...prev, zoom: zoom + 1 }))
    }
  }, [])

  // Get the inline style for the floor select container that matches the map size
  const getFloorSelectInlineStyle = useCallback(() => {
    // Keep floor selector width consistent regardless of map zoom/size
    const minWidth = 150
    const maxWidth = 260

    let baseWidth = matches ? 200 : 180

    if (window.innerWidth < 500) {
      baseWidth = Math.max(minWidth, window.innerWidth * 0.35)
    } else if (window.innerWidth > 1600) {
      baseWidth = maxWidth
    }

    const width = Math.min(Math.max(baseWidth, minWidth), maxWidth)

    return {
      backgroundColor: '#5F9EA0',
      width: `${width}px`,
      height: '40px',
      padding: '0rem',
      fontSize: matches ? '20px' : '10px'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle floor selection
  const handleSelectFloor = (value: number) => {
    isFloorChange.current = true
    // Reset states
    resetMap()
    // Update selected floor
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: undefined,
      selectedPoint: undefined,
      selectedFloor: value,
      isClickToNextFloor: true,
      isClickFromTourCarousel: false
    }))
  }

  // Handle marker click
  const handleMarkerClick = (point: any) => {
    setSelectedPointID(point.id)
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: `${point.areaId}`,
      selectedPoint: point.id,
      // Update selectedImage for panorama viewer
      selectedImage: point.image360?.id,
      isClickToDirection: true
    }))
  }

  const sync360ViewToArc = (icon: any) => {
    ;(window as any).sync_360viewToArc = (hlookat: any) => {
      const h = Krpano.NormalizeHGap(hlookat)
      radarDragFlag.current.rotate = Krpano.NormalizeHGap(
        h + radarDragFlag.current.hgap
      )
      povMarkerRef?.current?.setIcon(icon(radarDragFlag?.current?.rotate))
    }
  }

  // Handle window resize
  useEffect(() => {
    const handleResize = debounce(() => {
      if (mapContainerRef?.current) {
        mapContainerRef?.current.invalidateSize({ animate: true })

        if (imgBounds[0] > 0 && imgBounds[1] > 0) {
          const bounds = [
            [0, 0],
            [imgBounds[0], imgBounds[1]]
          ]

          mapContainerRef?.current.fitBounds(bounds, {
            animate: true,
            duration: 0.5
          })
        }
      }
    }, 200)

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)

      // Clean up any pending timers
      if (zoomTimerRef.current) {
        clearTimeout(zoomTimerRef.current)
      }
    }
  }, [imgBounds])

  // Handle zoom state changes
  useEffect(() => {
    if (mapContainerRef?.current && showMap && !isMapLoading) {
      setTimeout(() => {
        mapContainerRef?.current?.invalidateSize({ animate: true })

        if (imgBounds[0] > 0 && imgBounds[1] > 0) {
          const bounds = [
            [0, 0],
            [imgBounds[0], imgBounds[1]]
          ]

          mapContainerRef?.current.fitBounds(bounds, {
            animate: true,
            duration: 0.5
          })
        }
      }, 300)
    }
  }, [isZoomOutMap, showMap, isMapLoading, imgBounds])

  // Add zoom event listener
  useEffect(() => {
    if (!mapContainerRef?.current) return

    const map = mapContainerRef?.current
    const handleZoom = () => {
      const currentZoom = map.getZoom()
      const minZoom = map.getMinZoom()
      const maxZoom = map.getMaxZoom()
      setZoomValue({
        zoom: currentZoom,
        minZoom,
        maxZoom
      })
    }

    // Attach the zoom event listener
    map.on('zoom', handleZoom)

    // Clean up the event listener on unmount
    return () => {
      map.off('zoom', handleZoom)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapContainerRef?.current])

  // Initialize map when URL changes
  useEffect(() => {
    if (floorDetail?.mapFile?.s3Path) {
      resetMap()
      setTimeout(() => {
        initializeMap()
      }, 300)
    }
  }, [floorDetail?.mapFile?.s3Path, initializeMap, resetMap])

  useEffect(() => {
    if (!isMapLandscape && mapContainerRef?.current) {
      const currentZoom = mapContainerRef?.current.getZoom()
      const minZoom = mapContainerRef?.current.getMinZoom()
      const newZoom = Math.max(currentZoom - 0.5, minZoom)

      mapContainerRef?.current.setZoom(newZoom, {
        animate: true,
        duration: 0.5
      })
    }
  }, [isMapLandscape])

  // effect set default point when select floor from floor select in right  screen of tour viewer
  useEffect(() => {
    if (isFloorChange.current) {
      isFloorChange.current = false
      const defaultPoint = [...(points || [])].sort(
        (a, b) => a.order - b.order
      )[0]

      setCurrentViewer((prev) => ({
        ...prev,
        selectedPoint: defaultPoint?.id,
        selectedArea: `${defaultPoint?.areaId}`,
        selectedImage: defaultPoint?.image360?.id
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points])

  return {
    showMap,
    imgBounds,
    aspectRatio,
    isZoomOutMap,
    isMapLoading,
    currentViewer,
    isMapLandscape,
    mapContainerRef,
    resetMap,
    handleZoomIn,
    handleZoomOut,
    animateMapChange,
    handleZoomToggle,
    handleZoomToPoint,
    handleSelectFloor,
    setCurrentViewer,
    handleMarkerClick,
    getMapSizeInlineStyle,
    getFloorSelectInlineStyle,
    sync360ViewToArc,
    povMarkerRef,
    radarDragFlag,
    floorDetail,
    setFloorDetail,
    zoomValue
  }
}
